import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function Community() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/site-settings").then((r) => setSettings(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Both fields are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/community/whatsapp-signup", form);
      toast.success("You're on the list! We'll add you to the broadcast soon.");
      setForm({ name: "", phone: "" });
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="community-page">
      <section className="max-w-6xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-12">
        <div className="eyebrow mb-3">The pack</div>
        <h1 className="font-serif text-4xl sm:text-6xl text-brand-soft leading-[1.05] max-w-3xl">A small, warm community <span className="italic text-brand-maroon">for pet folks like you.</span></h1>
        <p className="mt-5 text-base sm:text-lg text-brand-soft/70 max-w-2xl">Get early event invites, drop-day alerts, and the occasional dog joke.</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 sm:px-12 pb-20 grid md:grid-cols-2 gap-6">
        {/* Telegram CTA */}
        <div className="bg-white rounded-3xl p-8 sm:p-10 border border-brand-maroon/10 flex flex-col justify-between" data-testid="telegram-card">
          <div>
            <Send className="w-7 h-7 text-brand-maroon mb-4" />
            <h2 className="font-serif text-3xl text-brand-soft">Join us on Telegram</h2>
            <p className="text-sm text-brand-soft/70 mt-3 leading-relaxed">A quiet channel for event drops, photos, and the occasional dog meme.</p>
          </div>
          <div className="mt-6">
            {settings?.telegram_link ? (
              <a href={settings.telegram_link} target="_blank" rel="noreferrer" className="btn-primary-furshala inline-flex items-center gap-2" data-testid="telegram-join">
                Open channel <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <div className="inline-block px-5 py-3 rounded-full bg-brand-ivory text-sm text-brand-soft/60">Link coming soon</div>
            )}
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="bg-brand-soft text-brand-cream rounded-3xl p-8 sm:p-10 flex flex-col justify-between" data-testid="whatsapp-card">
          <div>
            <MessageCircle className="w-7 h-7 text-brand-mustard mb-4" />
            <h2 className="font-serif text-3xl">Join WhatsApp channel</h2>
            <p className="text-sm text-brand-cream/70 mt-3 leading-relaxed">Be first to know about new collection drops and event bookings.</p>
          </div>
          <div className="mt-6">
            {settings?.whatsapp_link ? (
              <a href={settings.whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-brand-mustard text-brand-soft font-medium hover:bg-brand-mustard/90" data-testid="whatsapp-join">
                Open channel <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <div className="inline-block px-5 py-3 rounded-full bg-brand-cream/10 text-sm text-brand-cream/70">Link coming soon</div>
            )}
          </div>
        </div>
      </section>

      {/* Broadcast opt-in form */}
      <section className="bg-brand-ivory">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow mb-3">WhatsApp broadcast</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-brand-soft leading-tight">Want gentle nudges, not noise?</h2>
            <p className="text-brand-soft/70 mt-4 leading-relaxed">Drop your name and number — we&apos;ll add you to our manual broadcast for event invites and quiet drops. No spam, promise.</p>
          </div>
          <form onSubmit={submit} className="mt-10 max-w-xl mx-auto grid sm:grid-cols-2 gap-3" data-testid="broadcast-form">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="bg-white rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40 border border-brand-maroon/10"
              data-testid="broadcast-name"
            />
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="WhatsApp number"
              className="bg-white rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40 border border-brand-maroon/10"
              data-testid="broadcast-phone"
            />
            <button type="submit" disabled={submitting} className="btn-primary-furshala sm:col-span-2 inline-flex items-center justify-center gap-2" data-testid="broadcast-submit">
              {submitting ? "Adding…" : "Add me to the list"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
